import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsAlert } from 'src/components/portal-design-system/portal-ds-alert';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import { useDispatch } from 'react-redux';
import notificationService from 'src/services/notification/notification.service';
import { useTriggerCreativeImportMutation } from '../../endpoints/creative-import.endpoints';
import { storeCreativeImportRun } from '../../services/creative-import-run-storage';
import type { CreativeImportPreview } from '../../types/creative-import.types';
import { Client } from '../../types/client.types';

const LARGE_IMPORT_THRESHOLD = 50;

interface ImportCreativesModalProps {
  open: boolean;
  client: Client | null;
  preview: CreativeImportPreview | null;
  onOpenChange: (open: boolean) => void;
  onImportStarted: (runId: string) => void;
}

const extractImportError = (error: unknown): string => {
  if (!error || typeof error !== 'object') {
    return 'Failed to start creative import';
  }

  const record = error as {
    status?: number;
    responseData?: { detail?: unknown; error_code?: unknown };
    data?: { detail?: unknown; error_code?: unknown };
    message?: unknown;
  };

  if (record.status === 409) {
    const detail = record.responseData?.detail ?? record.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    return 'A creative import is already running for this client.';
  }

  const fromResponse = record.responseData?.detail ?? record.data?.detail;
  if (typeof fromResponse === 'string' && fromResponse.trim()) {
    return fromResponse;
  }

  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message;
  }

  return 'Failed to start creative import';
};

export const ImportCreativesModal: React.FC<ImportCreativesModalProps> = ({
  open,
  client,
  preview,
  onOpenChange,
  onImportStarted,
}) => {
  const dispatch = useDispatch();
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [triggerImport, { isLoading }] = useTriggerCreativeImportMutation();

  useEffect(() => {
    if (!open) {
      setInlineError(null);
    }
  }, [open]);

  if (!client || !preview) {
    return null;
  }

  const showLargeImportWarning = preview.creative_count > LARGE_IMPORT_THRESHOLD;

  const handleConfirm = async() => {
    setInlineError(null);
    try {
      const result = await triggerImport(client.ext_client_id).unwrap();
      const runId = result.run_id;
      storeCreativeImportRun({
        clientId: client.ext_client_id,
        runId,
        startedAt: new Date().toISOString(),
      });
      onOpenChange(false);
      onImportStarted(runId);
      notificationService.handleSuccess(
        result,
        dispatch,
        `Creative import started (run ${runId})`,
      );
    } catch (error) {
      setInlineError(extractImportError(error));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="sm:max-w-md bg-white"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className={portalDs.sectionTitle}>
          Import creatives
        </DialogTitle>
        <p className={portalDs.sheetDescription}>
          Import DSP creatives from the last 90 days into Studio for
          {' '}
          <strong>{client.title}</strong>
          .
        </p>
        <div className="mt-3 space-y-2 text-sm text-[#5E6278]">
          <p>
            <span className="font-semibold text-[#0D0F1A]">{preview.creative_count}</span>
            {' '}
            creatives with
            {' '}
            <span className="font-semibold text-[#0D0F1A]">{preview.variant_count}</span>
            {' '}
            variants will be imported.
          </p>
          <p className={portalDs.hint}>
            Counts cover all campaigns in the last 90 days, not per campaign.
          </p>
        </div>

        {showLargeImportWarning ? (
          <PortalDsAlert
            variant="warning"
            title="Large import"
            className="mt-3"
          >
            This client has more than 50 creatives in the last 90 days. The import may take
            a while and will add a significant number of items to the Studio library.
          </PortalDsAlert>
        ) : null}

        {inlineError ? (
          <PortalDsAlert
            variant="danger"
            title="Import failed"
            className="mt-3"
          >
            {inlineError}
          </PortalDsAlert>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <PortalDsButton
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </PortalDsButton>
          <PortalDsButton
            variant="primary"
            size="sm"
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isLoading}
          >
            {isLoading ? 'Starting import…' : 'Import creatives'}
          </PortalDsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportCreativesModal;
